import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Hero1 from '../assets/Hero-1.jpg';
import Hero4 from '../assets/Hero-4.jpg';
import KrinkelsK from '../Krinkels-K.png';
// icons for the status strip removed; RobotStatsSection renders its own icons
import ArrowRight from '../assets/assets/Arrow/Arrow_Right_SM.svg';
import '../styles/HomePage.css';
import '../styles/HomePage.extra.css';

import RobotInfoSection from '../components/homepage/RobotInfoSection';
import RobotStatsSection from '../components/homepage/RobotStatsSection';
import RobotModelsSection from '../components/homepage/RobotModelsSection';
import RobotServiceSection from '../components/homepage/RobotServiceSection';
import RobotContactBanner from '../components/homepage/RobotContactBanner';
import RBHLoginSection from '../components/homepage/RBHLoginSection';

// linter hint: ensure imported components are recognized in all linting configs
void (RobotInfoSection, RobotStatsSection, RobotModelsSection, RobotServiceSection, RobotContactBanner, RBHLoginSection);

const KRINKELS_LOGO_SRC = '/Krinkels-logo.png';
const BELROBOTICS_LOGO_SRC = '/belrobotics-logo.png';
const HUSQVARNA_LOGO_SRC = '/Huqvarna-Logo.png';
const KRESS_LOGO_SRC = '/Kress-Logo.png';
const KRINKELS_K_SRC = KrinkelsK;

export default function HomePage({ meldingen = [], homeOverviewStats = null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const derivedStats = React.useMemo(() => {
    const source = Array.isArray(meldingen) ? meldingen : [];
    const robotKeys = new Set(); const locationKeys = new Set();
    for (const item of source) {
      if (!item || typeof item !== 'object') continue;
      const robotKey = String(item.serialNumber || item.robotSerial || item.robotId || item.robot || item.robotNaam || item.displayName || '').trim().toLowerCase();
      if (robotKey) robotKeys.add(robotKey);
      const locationKey = String(item.branchName || item.branchId || item.vestiging || item.gemeente || item.location || '').trim().toLowerCase();
      if (locationKey) locationKeys.add(locationKey);
    }
    return { totalRobots: robotKeys.size, meldingenCount: source.length, locationsCount: locationKeys.size };
  }, [meldingen]);
  const stats = React.useMemo(() => {
    if (homeOverviewStats && homeOverviewStats.loaded) {
      return {
        activeRobots: Number(homeOverviewStats.activeRobots || 0),
        locationsCount: Number(homeOverviewStats.locationsCount || 0),
        distanceKm: homeOverviewStats.distanceKm,
        coveredAreaHa: homeOverviewStats.coveredAreaHa,
        footballFields: homeOverviewStats.footballFields,
        areaIsEstimated: homeOverviewStats.areaIsEstimated === true,
      };
    }
    // If no server stats and no incoming meldingen, treat as not-yet-loaded and show placeholders rather than 0
    if (!Array.isArray(meldingen) || meldingen.length === 0) return { activeRobots: null, locationsCount: null };
    return { activeRobots: Number(derivedStats.totalRobots || 0), locationsCount: Number(derivedStats.locationsCount || 0) };
  }, [homeOverviewStats, derivedStats]);
  // stats rendering is handled by RobotStatsSection to avoid duplicate strips and zero-fallbacks
  const cta = (label, action) => <button className="kr-btn kr-btn-brandbook" onClick={action}><span>{label}</span><span className="kr-btn-icon-box" aria-hidden><img src={ArrowRight} alt="" className="kr-btn-arrow" /></span></button>;

  const [isCompact, setIsCompact] = useState(false);
  const sentinelRef = useRef(null);
  const scrollStateRef = useRef(false);

  useEffect(() => {
    const imgs = [KRINKELS_LOGO_SRC, KRINKELS_K_SRC].map((src) => { const i = new Image(); i.src = src; return i; });
    const onScroll = (event) => {
      const source = event?.target && event.target !== document ? event.target : window;
      const y = source === window ? (window.scrollY || window.pageYOffset || 0) : source.scrollTop;
      const nextState = scrollStateRef.current
        ? y > 72
        : y >= 96;
      if (nextState !== scrollStateRef.current) {
        scrollStateRef.current = nextState;
        setIsCompact(nextState);
      }
    };
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    let initialTarget = window;
    let scrollCandidate = sentinelRef.current?.parentElement || null;
    while (scrollCandidate) {
      const overflowY = window.getComputedStyle(scrollCandidate).overflowY;
      if (['auto', 'scroll'].includes(overflowY) && scrollCandidate.scrollHeight > scrollCandidate.clientHeight) {
        initialTarget = scrollCandidate;
        break;
      }
      scrollCandidate = scrollCandidate.parentElement;
    }
    onScroll({ target: initialTarget });

    return () => {
      document.removeEventListener('scroll', onScroll, true);
      imgs.forEach((n) => { try { n.src = ''; } catch (e) { if (process.env.NODE_ENV !== 'production') console.warn('HomePage: preload cleanup error', e); } });
    };
  }, []);

  return (
    <main className="kr-home k-body">
      <div ref={sentinelRef} className="home-sentinel" aria-hidden="true" />
      {/* Public sticky header that swaps the full logo and the Krinkels-K on scroll */}
      <header className={`public-sticky-header ${isCompact ? 'is-compact' : 'is-expanded'}`} role="banner" aria-hidden={false}>
        <div className="public-sticky-inner public-sticky-container">
          <a className="public-brand-link" href="https://krinkels.nl" target="_blank" rel="noopener noreferrer" aria-label="Krinkels.nl">
            <img src={KRINKELS_LOGO_SRC} alt="Krinkels logo" className="public-logo public-logo-full" loading="eager" decoding="async" />
            <img src={KRINKELS_K_SRC} alt="Krinkels K" className="public-logo public-logo-mark" loading="eager" decoding="async" />
          </a>
        </div>
      </header>

      <header className="kr-hero" role="banner">
        <div className="kr-shell kr-hero-container">
          <div className="kr-hero-content">
            <h1 className="k-h1"><span className="hero-line">{t('homepageUi.text0')}</span><span className="hero-line hero-strong">{t('homepageUi.text1')}</span></h1>
            <p className="kr-lead">{t('homepageUi.text2')}</p>
            <div className="kr-hero-actions">{cta(t('homepageUi.text3'), () => navigate('/login'))}</div>
          </div>
          <div className="kr-hero-media"><img src={Hero1} alt="Krinkels in het veld" /></div>
        </div>
      </header>
      <section className="kr-section kr-visual"><div className="kr-shell kr-shell-wide"><div className="kr-visual-media"><img src={Hero4} alt="Krinkels werkzaamheden buiten" /></div></div></section>
      {/* Replace supporters and cta-strip with structured homepage sections */}
      <RobotInfoSection />
      <RobotStatsSection stats={stats} loaded={Boolean(homeOverviewStats && homeOverviewStats.loaded)} />
      <RobotModelsSection />
      <RobotServiceSection />
      <RBHLoginSection />
      <RobotContactBanner />
      <footer className="kr-footer"><div className="kr-shell kr-footer-row"><div><p>&copy; Krinkels BV</p><div className="kr-footer-brand-logos" aria-label="Partner logos"><img src={KRINKELS_LOGO_SRC} alt="Krinkels" className="kr-footer-brand-logo" loading="lazy" decoding="async" /><img src={BELROBOTICS_LOGO_SRC} alt="Belrobotics" className="kr-footer-brand-logo" loading="lazy" decoding="async" /><img src={HUSQVARNA_LOGO_SRC} alt="Husqvarna" className="kr-footer-brand-logo" loading="lazy" decoding="async" /><img src={KRESS_LOGO_SRC} alt="Kress" className="kr-footer-brand-logo" loading="lazy" decoding="async" /></div></div><small>{t('homepageUi.text11')}</small></div></footer>
    </main>
  );
}
